import { useEffect, useState, useCallback } from "react";
import { Eye, Trash2, Power, FileSpreadsheet, FileText, Download, X, Printer, Search, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { downloadApplicationPdf, downloadApplicationsListPdf, downloadApplicationsExcel } from "../../lib/applicationExport";
import PrintableApplication from "../PrintableApplication";
import { DEPARTMENT_OPTIONS } from "../../data/site";

const SEMS = ["Semester I", "Semester II", "Semester III", "Semester IV", "Semester V", "Semester VI", "Semester VII", "Semester VIII"];

export default function ApplicationsManager() {
  const { settings, reload: reloadSite } = useSite();
  const college = settings.college || {};
  const logo = (settings.images && settings.images.logo) || "";
  const forms = settings.forms || {};
  const [apps, setApps] = useState([]);
  const [ftype, setFtype] = useState("admission");
  const [view, setView] = useState(null);
  const [printApp, setPrintApp] = useState(null);
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState("All");
  const [sem, setSem] = useState("All");
  const [remark, setRemark] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.listApplications({ form_type: ftype }); setApps(data || []); } catch { /* */ }
  }, [ftype]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setQuery(""); setDept("All"); setSem("All"); }, [ftype]);
  useEffect(() => { setRemark(view?.remark || ""); }, [view]);

  const setStatus = async (app, status) => {
    try {
      await api.updateApplicationStatus(app.id, status, status === "rejected" ? remark.trim() : "");
      toast.success(status === "rejected" ? `Application rejected${app.email ? " & applicant emailed" : ""}` : "Application restored");
      setView(null);
      await load();
    } catch { toast.error("Failed to update status"); }
  };

  // One-click clean print: render the printable doc off-screen, then open print dialog.
  useEffect(() => {
    if (!printApp) return;
    const clear = () => setPrintApp(null);
    window.addEventListener("afterprint", clear, { once: true });
    const t = setTimeout(() => window.print(), 250);
    return () => { clearTimeout(t); window.removeEventListener("afterprint", clear); };
  }, [printApp]);

  const remove = async (id) => { try { await api.deleteApplication(id); toast.success("Deleted"); await load(); } catch { toast.error("Failed"); } };

  // The list is lean (no base64). Fetch & cache the full application on demand.
  const [fullCache, setFullCache] = useState({});
  const getFull = async (a) => {
    if (fullCache[a.id]) return fullCache[a.id];
    const { data } = await api.getApplication(a.id);
    setFullCache((c) => ({ ...c, [a.id]: data }));
    return data;
  };
  const viewApp = async (a) => { try { setView(await getFull(a)); } catch { toast.error("Could not load application"); } };
  const printApplication = async (a) => { try { setPrintApp(await getFull(a)); } catch { toast.error("Could not load application"); } };
  const downloadPdf = async (a) => { try { downloadApplicationPdf(await getFull(a), college, logo); } catch { toast.error("Could not load application"); } };
  const openFileFor = async (a, field) => {
    try {
      const full = await getFull(a);
      const url = field === "photo" ? full.photo : field === "signature" ? full.signature : (full.documents || {})[field];
      if (!url) { toast.error("File not found"); return; }
      openFile(url);
    } catch { toast.error("Could not load file"); }
  };

  // Browsers block opening data: URLs directly in a new tab. Convert to a Blob URL so the
  // photo / signature / document can be viewed (and printed with Ctrl+P) in a new tab.
  const openFile = (dataUrl) => {
    try {
      const [head, b64] = dataUrl.split(",");
      const mime = (head.match(/:(.*?);/) || [])[1] || "application/octet-stream";
      const bin = atob(b64);
      let n = bin.length;
      const u8 = new Uint8Array(n);
      while (n--) u8[n] = bin.charCodeAt(n);
      const url = URL.createObjectURL(new Blob([u8], { type: mime }));
      const w = window.open(url, "_blank");
      if (!w) toast.error("Please allow pop-ups to view the file");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open this file"); }
  };
  const toggle = async (type) => {
    try { await api.toggleForm(type, forms[type] === false); toast.success("Form window updated"); await reloadSite(); }
    catch { toast.error("Failed"); }
  };

  const q = query.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const filtered = apps.filter((a) => {
    if (dept !== "All" && (a.department || "") !== dept) return false;
    if (sem !== "All" && (a.semester || "") !== sem) return false;
    if (!q) return true;
    const name = (a.name || "").toLowerCase();
    const phone = (a.phone || "").replace(/\D/g, "");
    const phone2 = (a.phone_secondary || "").replace(/\D/g, "");
    const aadhar = (a.aadhar_no || "").toLowerCase();
    const aadharDigits = (a.aadhar_no || "").replace(/\D/g, "");
    return (
      name.includes(q) ||
      aadhar.includes(q) ||
      (qDigits && (phone.includes(qDigits) || phone2.includes(qDigits) || aadharDigits.includes(qDigits)))
    );
  });

  const filterLabel = [dept !== "All" ? dept : "", sem !== "All" ? sem : ""].filter(Boolean).join(" · ");
  const exportExcel = () => {
    if (!filtered.length) { toast.error("No applications to export"); return; }
    downloadApplicationsExcel(filtered, ftype, filterLabel); toast.success("Excel downloaded");
  };
  const exportListPdf = () => {
    if (!filtered.length) { toast.error("No applications to export"); return; }
    downloadApplicationsListPdf(filtered, ftype, college, filterLabel); toast.success("PDF downloaded");
  };

  return (
    <div data-testid="applications-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h2 className="font-serif text-2xl text-forest">Applications</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="toggle-form-admission" onClick={() => toggle("admission")} className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border ${forms.admission === false ? "bg-destructive/10 text-destructive border-destructive/30" : "bg-forest/10 text-forest border-forest/30"}`}><Power size={13} /> Admission {forms.admission === false ? "Closed" : "Open"}</button>
          <button data-testid="toggle-form-registration" onClick={() => toggle("registration")} className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border ${forms.registration === false ? "bg-destructive/10 text-destructive border-destructive/30" : "bg-forest/10 text-forest border-forest/30"}`}><Power size={13} /> Registration {forms.registration === false ? "Closed" : "Open"}</button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {["admission", "registration"].map((t) => (
            <button key={t} data-testid={`apps-tab-${t}`} onClick={() => setFtype(t)} className={`text-xs px-4 py-1.5 rounded-full border capitalize transition-colors ${ftype === t ? "bg-forest text-bone border-forest" : "border-line text-muted hover:border-forest"}`}>{t}{ftype === t ? ` (${apps.length})` : ""}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="export-excel-btn" onClick={exportExcel} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-forest/30 text-forest hover:bg-forest hover:text-bone transition-colors"><FileSpreadsheet size={14} /> Export Excel</button>
          <button data-testid="export-pdf-btn" onClick={exportListPdf} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-sm border border-forest/30 text-forest hover:bg-forest hover:text-bone transition-colors"><FileText size={14} /> Export PDF</button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            data-testid="application-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone or Aadhar…"
            className="w-full bg-white border border-line focus:border-gold outline-none pl-9 pr-9 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30"
          />
          {query && (
            <button data-testid="application-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-forest" title="Clear"><X size={15} /></button>
          )}
        </div>
        <select data-testid="application-dept-filter" value={dept} onChange={(e) => setDept(e.target.value)} className="bg-white border border-line focus:border-gold outline-none px-3 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30">
          <option value="All">All departments</option>
          {DEPARTMENT_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select data-testid="application-sem-filter" value={sem} onChange={(e) => setSem(e.target.value)} className="bg-white border border-line focus:border-gold outline-none px-3 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30">
          <option value="All">All semesters</option>
          {SEMS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {(query || dept !== "All" || sem !== "All") && <p data-testid="search-result-count" className="text-xs text-muted mb-3">Showing {filtered.length} of {apps.length} {ftype} application{apps.length !== 1 ? "s" : ""}</p>}

      <div className="border border-line bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone text-forest/60 uppercase text-xs tracking-widest">
            <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Aadhar</th><th className="text-left px-4 py-3">{ftype === "registration" ? "Semester" : "Course"}</th><th className="text-left px-4 py-3">Uploads (Photo / Sign / Docs)</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filtered.map((a) => {
              const docKeys = a.doc_keys || [];
              const hasFiles = a.has_photo || a.has_signature || docKeys.length;
              return (
              <tr key={a.id} data-testid="application-row">
                <td className="px-4 py-3 font-medium text-forest">{a.name}</td>
                <td className="px-4 py-3 font-mono">{a.phone}</td>
                <td className="px-4 py-3 font-mono text-muted">{a.aadhar_no}</td>
                <td className="px-4 py-3 text-muted">{ftype === "registration" ? a.semester : a.course}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5 max-w-[280px]" data-testid="app-files-cell">
                    {a.has_photo && <button type="button" onClick={() => openFileFor(a, "photo")} data-testid="file-photo" className="text-[11px] px-2 py-1 rounded-full border border-line text-forest hover:bg-forest hover:text-bone transition-colors">Photo</button>}
                    {a.has_signature && <button type="button" onClick={() => openFileFor(a, "signature")} data-testid="file-signature" className="text-[11px] px-2 py-1 rounded-full border border-line text-forest hover:bg-forest hover:text-bone transition-colors">Sign</button>}
                    {docKeys.map((k) => <button type="button" key={k} onClick={() => openFileFor(a, k)} data-testid={`file-${k}`} className="text-[11px] px-2 py-1 rounded-full border border-line text-forest hover:bg-forest hover:text-bone transition-colors capitalize">{k}</button>)}
                    {!hasFiles && <span className="text-[11px] text-muted">—</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {a.status === "rejected"
                    ? <span data-testid="status-badge-rejected" className="text-[11px] px-2.5 py-1 rounded-full bg-destructive/10 text-destructive border border-destructive/30">Rejected</span>
                    : <span data-testid="status-badge-submitted" className="text-[11px] px-2.5 py-1 rounded-full bg-forest/10 text-forest border border-forest/20">Submitted</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 justify-end">
                    <button data-testid="view-app-btn" onClick={() => viewApp(a)} className="w-8 h-8 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone" title="View"><Eye size={14} /></button>
                    <button data-testid="print-app-btn" onClick={() => printApplication(a)} className="w-8 h-8 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone" title="Print"><Printer size={14} /></button>
                    <button data-testid="download-app-pdf-btn" onClick={() => downloadPdf(a)} className="w-8 h-8 flex items-center justify-center border border-line rounded-sm hover:bg-forest hover:text-bone" title="Download PDF"><Download size={14} /></button>
                    <button data-testid="delete-app-btn" onClick={() => remove(a.id)} className="w-8 h-8 flex items-center justify-center border border-line rounded-sm hover:bg-destructive hover:text-white" title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="p-8 text-muted">{apps.length === 0 ? `No ${ftype} applications yet.` : "No applications match your search / filter."}</p>}
      </div>

      {view && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm" onClick={() => setView(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl rounded-sm p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <h3 className="font-serif text-2xl text-forest">{view.name}</h3>
              <button onClick={() => setView(null)} className="text-muted hover:text-forest"><X size={22} /></button>
            </div>
            <div className="flex gap-4 mb-4">
              {view.photo && <img src={view.photo} alt="photo" className="w-24 h-28 object-cover border border-line rounded-sm" />}
              {view.signature && <div><p className="text-xs text-muted mb-1">Signature</p><img src={view.signature} alt="sign" className="h-16 object-contain border border-line rounded-sm" /></div>}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[["Father", view.father_name], ["Mother", view.mother_name], ["DOB", view.dob], view.form_type === "registration" ? ["Semester", view.semester] : ["Course", view.course], ["Department", view.department], ["Roll No", view.roll_no], ["ABC ID", view.abc_id], ["Aadhar", view.aadhar_no], ["Reg No", view.registration_no], ["Email", view.email], ["Phone", view.phone], ["Phone 2", view.phone_secondary], ["Present", view.present_address], ["Permanent", view.permanent_address]].filter((r) => r[1]).map(([k, v]) => (
                <div key={k}><span className="text-muted uppercase text-[10px] tracking-widest">{k}</span><p className="text-forest">{v}</p></div>
              ))}
            </div>
            {view.documents && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-forest/60 mb-2">Documents</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(view.documents).filter(([k, v]) => k !== "extras" && v).map(([k, v]) => <button type="button" key={k} onClick={() => openFile(v)} className="text-xs border border-line px-3 py-1.5 rounded-full text-forest hover:bg-bone capitalize">{k}</button>)}
                  {(view.documents.extras || []).map((d, i) => d.file && <button type="button" key={i} onClick={() => openFile(d.file)} className="text-xs border border-line px-3 py-1.5 rounded-full text-forest hover:bg-bone">{d.label || "Doc"}</button>)}
                </div>
              </div>
            )}
            <div className="mt-6 flex gap-2">
              <button data-testid="modal-print-btn" onClick={() => { setPrintApp(view); setView(null); }} className="flex-1 inline-flex items-center justify-center gap-2 border border-forest text-forest py-3 rounded-full text-sm uppercase tracking-widest hover:bg-forest hover:text-bone transition-colors"><Printer size={16} /> Print</button>
              <button data-testid="modal-download-pdf-btn" onClick={() => downloadApplicationPdf(view, college, logo)} className="flex-1 inline-flex items-center justify-center gap-2 bg-forest text-bone py-3 rounded-full text-sm uppercase tracking-widest"><Download size={16} /> Download PDF</button>
            </div>

            <div className="mt-6 border-t border-line pt-5" data-testid="admin-decision">
              <p className="text-xs uppercase tracking-widest text-forest/60 mb-2">Admin Decision</p>
              {view.status === "rejected" ? (
                <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-4">
                  <p className="text-sm text-destructive font-medium">This application was rejected.</p>
                  {view.remark && <p className="text-sm text-forest mt-1"><span className="text-muted">Reason:</span> {view.remark}</p>}
                  <button data-testid="restore-app-btn" onClick={() => setStatus(view, "submitted")} className="mt-3 inline-flex items-center gap-2 text-xs border border-line px-4 py-2 rounded-full text-forest hover:bg-bone"><RotateCcw size={14} /> Restore (mark as submitted)</button>
                </div>
              ) : (
                <div>
                  <textarea data-testid="reject-remark-input" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Reason for rejection — this is emailed to the applicant and shown on their status page." rows={3} className="w-full bg-white border border-line focus:border-gold outline-none px-4 py-2.5 rounded-sm text-sm focus:ring-2 focus:ring-gold/30" />
                  <button data-testid="reject-app-btn" onClick={() => { if (!remark.trim()) { toast.error("Please add a reason for rejection"); return; } setStatus(view, "rejected"); }} className="mt-3 inline-flex items-center gap-2 bg-destructive text-white px-5 py-2.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform"><Ban size={15} /> Reject & notify applicant</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {printApp && (
        <div className="print-doc" data-testid="printable-application">
          <PrintableApplication app={printApp} college={college} logo={logo} />
        </div>
      )}
    </div>
  );
}
